import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

/**
 * Canned responses have no dedicated category field in the schema (only
 * `short_code`/`content`) - this derives a category from the existing
 * `short_code` naming convention instead of a migration: a short_code like
 * `billing/refund-policy` groups under "billing"; a short_code with no `/`
 * (e.g. `hello`) falls into UNCATEGORIZED_KEY. Purely a display-layer
 * grouping over the existing field - fully backward compatible with every
 * short_code that already exists, and trivially replaceable with a real
 * `category` column/model later without changing any consumer's API surface
 * (this file's exports are the only thing that would need to change).
 */
export const UNCATEGORIZED_KEY = '__uncategorized__';

export const categoryFor = shortCode => {
  const slashIndex = (shortCode || '').indexOf('/');
  if (slashIndex <= 0) return UNCATEGORIZED_KEY;
  return shortCode.slice(0, slashIndex);
};

export const nameWithoutCategory = shortCode => {
  const slashIndex = (shortCode || '').indexOf('/');
  if (slashIndex <= 0) return shortCode;
  return shortCode.slice(slashIndex + 1);
};

/**
 * Groups canned responses by their derived category, sorted alphabetically
 * with UNCATEGORIZED_KEY always last.
 * @param {Array} cannedResponses - objects with a `short_code` field.
 */
export const groupByCategory = cannedResponses => {
  const groups = new Map();
  cannedResponses.forEach(response => {
    const category = categoryFor(response.short_code);
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(response);
  });
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === UNCATEGORIZED_KEY) return 1;
    if (b === UNCATEGORIZED_KEY) return -1;
    return a.localeCompare(b);
  });
};

const MAX_RECENTLY_USED = 5;

/**
 * Per-agent recently-used and favorite canned-response tracking, persisted
 * via the existing `ui_settings` JSONB blob (same storage already used for
 * sidebar item order, signature flags, etc.) - no new table/column needed,
 * and independent of the category grouping above per design.
 */
export function useCannedResponseUsage() {
  const { uiSettings, updateUISettings } = useUISettings();

  const recentlyUsedCodes = computed(
    () => uiSettings.value.recently_used_canned_responses || []
  );
  const favoriteCodes = computed(
    () => uiSettings.value.favorite_canned_responses || []
  );

  const recordUsage = shortCode => {
    const withoutCurrent = recentlyUsedCodes.value.filter(
      code => code !== shortCode
    );
    updateUISettings({
      recently_used_canned_responses: [shortCode, ...withoutCurrent].slice(
        0,
        MAX_RECENTLY_USED
      ),
    });
  };

  const isFavorite = shortCode => favoriteCodes.value.includes(shortCode);

  const toggleFavorite = shortCode => {
    const isCurrentlyFavorite = isFavorite(shortCode);
    updateUISettings({
      favorite_canned_responses: isCurrentlyFavorite
        ? favoriteCodes.value.filter(code => code !== shortCode)
        : [...favoriteCodes.value, shortCode],
    });
  };

  return {
    recentlyUsedCodes,
    favoriteCodes,
    recordUsage,
    isFavorite,
    toggleFavorite,
  };
}
